<div class="page-container">
  <div class="container">
    <app-page-header
      [title]="mode === 'create' ? 'Create Campaign' : 'Edit Campaign'"
      subtitle="Configure your radio advertising campaign"
      [showBackButton]="true"
      backRoute="/orders"
      backLabel="Back to Orders">
    </app-page-header>

    <div class="builder-content">
      @if (pendingPromo) {
        <div class="promo-lock-banner" role="status">
          <mat-icon aria-hidden="true">local_offer</mat-icon>
          <div class="promo-lock-banner-text">
            <strong>Using promo "{{ pendingPromo.code }}"</strong>
            <span>
              Station is locked to <strong>{{ pendingPromo.stationName }}</strong>.
              Change station to drop this promo.
            </span>
          </div>
          <button
            type="button"
            class="btn-secondary promo-lock-clear-btn"
            (click)="clearPendingPromo()">
            Remove promo
          </button>
        </div>
      }

      <section class="step-card">
        <div class="step-header">
          <div class="step-number" aria-hidden="true">1</div>
          <h3>{{ isUserOrderFlow ? 'Coverage Cities & Station' : 'Station, Recordings & Station Rate' }}</h3>
        </div>

        <div class="step-content">
          @if (isUserOrderFlow) {
            <app-coverage-city-select
              [multiple]="true"
              [required]="true"
              [stationCoverageOnly]="true"
              label="Coverage Cities"
              placeholder="Search and select cities"
              hint="Only cities with active station coverage are listed"
              [(ngModel)]="selectedCoverageCities"
              (citiesChange)="onCoverageCitiesChange($event)">
            </app-coverage-city-select>

            @if (selectedCoverageCities.length > 0) {
              @if (loadingStations) {
                <div class="inline-loading" role="status">
                  <mat-spinner diameter="22"></mat-spinner>
                  <span>Loading stations for selected cities…</span>
                </div>
              } @else if (stationsLoadError) {
                <div class="inline-error" role="alert">{{ stationsLoadError }}</div>
              } @else if (stations.length === 0) {
                <div class="inline-empty" role="status">No stations found for selected cities.</div>
              } @else {
                <div class="station-selection-block">
                  <app-station-select-field
                    class="full-width"
                    [stations]="stations"
                    [(stationId)]="selectedStationId"
                    label="Station"
                    placeholder="Search and select station"
                    [required]="true"
                    (stationIdChange)="onStationChange()" />

                  @if (selectedStation) {
                    <button
                      type="button"
                      class="coverage-map-link"
                      (click)="openCoverageMapDialog()"
                      [attr.aria-label]="'View coverage map for ' + selectedStation.name">
                      <mat-icon aria-hidden="true">map</mat-icon>
                      View Coverage Map
                    </button>
                  }
                </div>
              }
            }
          } @else {
            <div class="station-selection-block">
              @if (loadingStations) {
                <div class="skeleton-field"></div>
              } @else {
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Station</mat-label>
                  <mat-select [(ngModel)]="selectedStationId" (selectionChange)="onStationChange()">
                    @for (station of stations; track station.id) {
                      <mat-option [value]="station.id">
                        {{ station.call_sign }} - {{ station.name }}
                      </mat-option>
                    }
                  </mat-select>
                  <mat-icon matSuffix>radio</mat-icon>
                </mat-form-field>
              }
            </div>
          }

          @if (selectedStationId) {
            <div class="recordings-section">
              @if (loadingRecordings) {
                <div class="skeleton-field"></div>
              } @else if (recordings.length === 0) {
                <p class="field-help-empty" role="status">
                  No approved recordings are available for the selected station.
                </p>
              } @else {
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Approved Recordings</mat-label>
                  <mat-select [(ngModel)]="selectedRecordingIds" multiple (selectionChange)="onRecordingsChange()">
                    @for (recording of recordings; track recording.id) {
                      <mat-option [value]="recording.id"
                        [disabled]="selectedRecordingIds.length > 0 && getSpotLengthCategoryPublic(recording.audiolength) !== getSpotLengthCategoryPublic(getRecordingById(selectedRecordingIds[0])?.audiolength || 0)">
                        {{ recording.name | titlecase }} - {{ recording.station_name }} ({{ recording.audiolength }}s)
                      </mat-option>
                    }
                  </mat-select>
                  <mat-icon matSuffix>audiotrack</mat-icon>
                </mat-form-field>
              }

              @if (selectedRecordingIds.length > 0) {
                <div class="selected-recordings">
                  <strong>Selected: </strong>
                  @for (id of selectedRecordingIds; track id) {
                    <span class="recording-chip">
                      {{ getRecordingName(id) }}
                    </span>
                  }
                </div>
              }

              @if (selectedRecordingIds.length > 0) {
                @if (loadingRates) {
                  <div class="skeleton-field"></div>
                } @else {
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Station Rate</mat-label>
                    <mat-select [(ngModel)]="selectedStationRateId" (selectionChange)="onRateChange()"
                      [disabled]="filteredStationRates.length === 0">
                      @if (filteredStationRates.length === 0) {
                        <mat-option disabled>No rate for this spot length</mat-option>
                      } @else {
                        @for (rate of filteredStationRates; track rate.id) {
                          <mat-option [value]="rate.id">
                            {{ rate.friendly_name }} - {{ rate.spot_length }}s - {{ stationRatesSvc.formatCurrency(rate.ratecard) }}
                          </mat-option>
                        }
                      }
                    </mat-select>
                    <mat-icon matSuffix>schedule</mat-icon>
                  </mat-form-field>
                }
              }
            </div>
          }
        </div>
      </section>

      <section class="step-card" [hidden]="!selectedStationRateId">
        <div class="step-header">
          <div class="step-number" aria-hidden="true">2</div>
          <h3>Campaign Duration</h3>
        </div>

        <div class="step-content">
          <mat-form-field appearance="outline" class="full-width" (click)="rangePicker.open()">
            <mat-label>Date Range</mat-label>
            <mat-date-range-input
              [rangePicker]="rangePicker"
              [min]="calendarMinDate"
              [dateFilter]="availableDateFilter"
              [disabled]="!datesReadyForScheduling">
              <input matStartDate placeholder="Start date" [(ngModel)]="startDate" (dateChange)="onRangeChange()"
                readonly (click)="rangePicker.open()">
              <input matEndDate placeholder="End date" [(ngModel)]="endDate" (dateChange)="onRangeChange()" readonly
                (click)="rangePicker.open()">
            </mat-date-range-input>
            <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
            <mat-date-range-picker #rangePicker [startAt]="calendarStartAt"></mat-date-range-picker>
            <mat-hint>
              @if (loadingLockedDates || loadingOrderRule) {
              Loading station schedule dates…
              } @else if (earliestStartIso) {
              Select start then end date. The earliest start for this station is {{ earliestStartIso }} (order lead time); locked days cannot be selected.
              } @else {
              Select start then end date. The current business day ({{ businessTodayIso || 'today' }}) and locked days cannot be selected; only future unlocked dates are allowed.
              }
            </mat-hint>
          </mat-form-field>
        </div>
      </section>

      <section class="step-card" [hidden]="!startDate || !endDate">
        <div class="step-header">
          <div class="step-number" aria-hidden="true">3</div>
          <h3>Scheduling Goal</h3>
        </div>

        <div class="step-content">
          <mat-radio-group [(ngModel)]="schedulingMode" class="scheduling-mode-group">
            <mat-radio-button value="target_quantity">Target Quantity (Number of Ads)</mat-radio-button>
            <mat-radio-button value="target_impressions">Target Impressions</mat-radio-button>
          </mat-radio-group>

          @if (loadingMaximums) {
          <div class="skeleton-slider-container">
            <div class="skeleton-label"></div>
            <div class="skeleton-slider"></div>
            <div class="skeleton-hint"></div>
          </div>
          } @else if (maximums) {
          @if (schedulingMode === 'target_quantity') {
          <div class="slider-container">
            <div class="slider-row">
              <mat-slider class="modern-slider" min="0" [max]="maximums.max_ads || 100" step="1" discrete>
                <input matSliderThumb [(ngModel)]="targetQuantity">
              </mat-slider>
              <mat-form-field appearance="outline" class="manual-input">
                <input matInput type="number" [(ngModel)]="targetQuantity" min="0" [max]="maximums.max_ads || 100"
                  (change)="onTargetQuantityChange()">
              </mat-form-field>
            </div>
            <mat-hint class="max-hint">Maximum available: {{ maximums.max_ads }} ads</mat-hint>
          </div>
          } @else {
          <div class="slider-container">
            <div class="slider-row">
              <mat-slider class="modern-slider" min="0" [max]="maximums.max_impressions || 100000"
                [step]="impressionStep" discrete>
                <input matSliderThumb [(ngModel)]="targetImpressions">
              </mat-slider>
              <mat-form-field appearance="outline" class="manual-input">
                <input matInput type="number" [(ngModel)]="targetImpressions" min="0"
                  [max]="maximums.max_impressions || 100000" (change)="onTargetImpressionsChange()">
              </mat-form-field>
            </div>
            <mat-hint class="max-hint">Maximum available: {{ formatNumber(maximums.max_impressions) }}
              impressions</mat-hint>
          </div>
          }
          }
        </div>
      </section>

      @if (maximums) {
      @if (selectedStationRateId) {
      @if (schedulingMode === 'target_quantity') {
      @if (targetQuantity > 0) {
      <section class="estimated-cost-card" aria-live="polite">
        <div class="cost-content">
          <div class="cost-icon-wrapper">
            <mat-icon>monetization_on</mat-icon>
          </div>
          <div class="cost-details">
            <div class="cost-label">Estimated Campaign Cost</div>
            <div class="cost-display">{{ formatCurrency(estimatedCost || 0) }}</div>
            <div class="cost-hint">
              Real-time estimate for {{ targetQuantity }} ad@if (targetQuantity > 1) {s}
            </div>
            <div class="cost-note">
              <mat-icon>info</mat-icon>
              <span>This is a real-time estimate. Final cost will be shown in preview.</span>
            </div>
          </div>
        </div>
      </section>
      }
      } @else {
      @if (targetImpressions > 0) {
      <section class="estimated-cost-card" aria-live="polite">
        <div class="cost-content">
          <div class="cost-icon-wrapper">
            <mat-icon>monetization_on</mat-icon>
          </div>
          <div class="cost-details">
            <div class="cost-label">Estimated Campaign Cost</div>
            <div class="cost-display">{{ formatCurrency(estimatedCost || 0) }}</div>
            <div class="cost-hint">
              Real-time estimate for {{ formatNumber(targetImpressions) }} impressions
            </div>
            <div class="cost-note">
              <mat-icon>info</mat-icon>
              <span>This is a real-time estimate. Final cost will be shown in preview.</span>
            </div>
          </div>
        </div>
      </section>
      }
      }
      }
      }

      <div class="action-buttons">
        <button type="button" class="btn-secondary" (click)="cancel()" [disabled]="submitting || loadingPreview">
          <mat-icon aria-hidden="true">close</mat-icon>
          <span class="btn-label">Cancel</span>
        </button>
        <button
          type="button"
          class="btn-primary"
          (click)="previewCampaign()"
          [disabled]="!canPreview() || loadingPreview">
          @if (loadingPreview) {
            <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
          } @else {
            <mat-icon aria-hidden="true">preview</mat-icon>
          }
          <span class="btn-label">Preview Campaign</span>
        </button>
      </div>
    </div>
  </div>
</div>

@if (showCoverageMapDialog && selectedStation) {
  <div class="coverage-map-overlay" (click)="closeCoverageMapDialog()" aria-modal="true" role="dialog">
    <div class="coverage-map-dialog" (click)="$event.stopPropagation()">
      <div class="coverage-map-dialog-header">
        <div>
          <h3>{{ selectedStation.name }}</h3>
          <p>{{ formatStationCoverage(selectedStation) }}</p>
        </div>
        <button type="button" mat-icon-button (click)="closeCoverageMapDialog()" aria-label="Close coverage map dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="coverage-map-dialog-body">
        @if (selectedStation.coverage_map_url) {
          <img [src]="selectedStation.coverage_map_url" [alt]="selectedStation.name + ' coverage map'" />
        } @else {
          <div class="coverage-map-missing">Coverage map not available.</div>
        }
      </div>
    </div>
  </div>
}
